import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import {
  Poi,
  PoiType,
  SingletonPoi,
  StandardPoi,
  StandardPoiType,
} from 'features/mapping/mapping.types';
import {
  POI_TYPE_ORDER,
  MUTUALLY_EXCLUSIVE_POI_TYPES,
} from 'features/mapping/mapClick/priority';

type SelectedPoisState = {
  selectedPois: Poi[];
  activeIndex: number;
};

type UsePoisState = {
  selectedPois: Poi[];
  selectedPoi?: Poi;
  activeIndex: number;
  setSelectedSingletonPoi: (poi: SingletonPoi) => void;
  setSelectedPois: (pois: StandardPoi[]) => void;
  clearSelectedPois: () => void;
  clearSelectedPoisOfType: (type: PoiType) => void;
  selectPreviousPoi: () => void;
  selectNextPoi: () => void;
};

// POI dialog order is derived from the single map-click priority policy (see
// mapClick/priority) and re-exported here so existing `state/usePoisState` importers keep
// working.
export { POI_TYPE_ORDER };

const DEFAULT_SELECTED_POIS_STATE: SelectedPoisState = {
  selectedPois: [],
  activeIndex: 0,
};

const poiStateAtom = atom({
  key: 'POIS_STATE',
  default: DEFAULT_SELECTED_POIS_STATE,
});

export const sortPoisByType = (
  pois: StandardPoi[],
  order: StandardPoiType[],
): StandardPoi[] =>
  pois.sort((a, b) => {
    const aIndex = order.indexOf(a.type);
    const bIndex = order.indexOf(b.type);
    return aIndex - bIndex;
  });

const isExclusive = (a: Poi, b: Poi): boolean => {
  const aExcludes = MUTUALLY_EXCLUSIVE_POI_TYPES[a.type] ?? [];
  const bExcludes = MUTUALLY_EXCLUSIVE_POI_TYPES[b.type] ?? [];
  return aExcludes.includes(b.type) || bExcludes.includes(a.type);
};

// Handle mutually exclusive POI types
// Some POI types are incompatible with each other and cannot be selected simultaneously.
// This function filters out selected POIs that are mutually exclusive.
export const filterMutuallyExclusivePois = <T extends Poi>(pois: T[]): T[] => {
  const selectedPois: T[] = [];
  for (const poi of pois) {
    const isMutuallyExclusive = selectedPois.some((selected) =>
      isExclusive(poi, selected),
    );
    if (!isMutuallyExclusive) selectedPois.push(poi);
  }
  return selectedPois;
};

export const usePoisState = (): UsePoisState => {
  const [poiState, setPoiState] = useRecoilState(poiStateAtom);

  const setSelectedSingletonPoi = useCallback(
    (poi: SingletonPoi) => {
      setPoiState({ selectedPois: [poi], activeIndex: 0 });
    },
    [setPoiState],
  );

  const setSelectedPois = useCallback(
    (pois: StandardPoi[]) => {
      // Order matters: sort first, then filter
      const sortedPois = sortPoisByType(pois, POI_TYPE_ORDER);
      const filteredPois = filterMutuallyExclusivePois(sortedPois);
      setPoiState({ selectedPois: filteredPois, activeIndex: 0 });
    },
    [setPoiState],
  );

  const clearSelectedPois = useCallback(() => {
    setPoiState({ selectedPois: [], activeIndex: 0 });
  }, [setPoiState]);

  const clearSelectedPoisOfType = useCallback(
    (type: PoiType) => {
      setPoiState((prev) => {
        const filteredPois = prev.selectedPois.filter(
          (poi) => poi.type !== type,
        );
        return {
          ...prev,
          selectedPois: filteredPois,
          activeIndex: 0,
        };
      });
    },
    [setPoiState],
  );

  const selectPreviousPoi = useCallback(() => {
    setPoiState((prev) => ({
      ...prev,
      activeIndex: Math.max(0, prev.activeIndex - 1),
    }));
  }, [setPoiState]);

  const selectNextPoi = useCallback(() => {
    setPoiState((prev) => ({
      ...prev,
      activeIndex: Math.min(prev.selectedPois.length - 1, prev.activeIndex + 1),
    }));
  }, [setPoiState]);

  const selectedPoi = poiState.selectedPois[poiState.activeIndex];

  return {
    selectedPois: poiState.selectedPois,
    selectedPoi,
    activeIndex: poiState.activeIndex,
    setSelectedSingletonPoi,
    setSelectedPois,
    clearSelectedPois,
    clearSelectedPoisOfType,
    selectPreviousPoi,
    selectNextPoi,
  };
};
